import { useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import {
  ECouncilorRole,
  ICouncilCamera,
  ICouncilCouncilor,
  ISaveCouncilCouncilorRequest,
} from "dto/Council/ICouncilPortal";

import { decodeText } from "./decodeText";
import styles from "./styles.module.scss";

interface Props {
  /** null → nowy radny; obiekt → edycja. */
  councilor: ICouncilCouncilor | null;
  /** Kamery skonfigurowane w ustawieniach; pusta lista ukrywa sekcję kamery. */
  cameras?: ICouncilCamera[];
  loading?: boolean;
  onClose: () => void;
  onApply: (request: ISaveCouncilCouncilorRequest) => void;
}

// Kartoteka radnego — odwzorowanie CouncilorModal z Magistratu.
//
// Różnica: w Magistracie konto portalu i kartoteka radnego były osobnymi bytami
// w dwóch bazach; tutaj to jeden wiersz, więc login i hasło są tu, a nie w drugim oknie.

const LOGIN_PATTERN = /^[A-Za-z0-9._@-]{3,100}$/;

const toDateInput = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";

const CouncilorModal = ({
  councilor,
  cameras = [],
  loading = false,
  onClose,
  onApply,
}: Props) => {
  const [firstName, setFirstName] = useState(decodeText(councilor?.FirstName));
  const [lastName, setLastName] = useState(decodeText(councilor?.LastName));
  const [email, setEmail] = useState(decodeText(councilor?.Email));
  const [phone, setPhone] = useState(decodeText(councilor?.Phone));
  const [login, setLogin] = useState(decodeText(councilor?.Login));
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<ECouncilorRole>(
    councilor?.Role ?? ECouncilorRole.None,
  );
  const [male, setMale] = useState(councilor?.Male ?? true);
  const [cadenceStart, setCadenceStart] = useState(toDateInput(councilor?.CadenceStart));
  const [cadenceEnd, setCadenceEnd] = useState(toDateInput(councilor?.CadenceEnd));
  const [isActive, setIsActive] = useState(councilor?.IsActive ?? true);

  // Kamera i preset radnego — Councilor.CameraId / CameraPreset z Magistratu.
  // Przy udzieleniu głosu kamera najeżdża na ten preset, a OBS przechodzi na scenę
  // przypisaną tej kamerze.
  const [cameraId, setCameraId] = useState(councilor?.CameraId ?? "");
  const [cameraPreset, setCameraPreset] = useState(
    councilor?.CameraPreset != null ? String(councilor.CameraPreset) : "",
  );

  const loginValid = LOGIN_PATTERN.test(login.trim());

  // Nowe konto bez hasła nie zaloguje się do portalu, więc przy zakładaniu hasło
  // jest wymagane; przy edycji puste pole oznacza „nie zmieniaj".
  const passwordValid = councilor ? !password || password.length >= 8 : password.length >= 8;

  const canApply = !!lastName.trim() && loginValid && passwordValid;

  const submit = () => {
    if (!canApply) return;

    onApply({
      Id: councilor?.Id ?? null,
      FirstName: firstName.trim() || null,
      LastName: lastName.trim() || null,
      Email: email.trim() || null,
      Phone: phone.trim() || null,
      Login: login.trim(),
      Role: role,
      Male: male,
      CadenceStart: cadenceStart || null,
      CadenceEnd: cadenceEnd || null,
      IsActive: isActive,
      Password: password || null,
      CameraId: cameraId || null,
      CameraPreset: cameraPreset.trim() === "" ? null : Number(cameraPreset),
    });
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>
          {councilor ? "Edytuj radnego" : "Nowy radny"}
        </div>

        <div className={styles.modalBody}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Imię</label>
              <input
                className={styles.textInput}
                value={firstName}
                autoFocus
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Nazwisko</label>
              <input
                className={styles.textInput}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>E-mail</label>
              <input
                className={styles.textInput}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Telefon</label>
              <input
                className={styles.textInput}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Login do portalu</label>
              <input
                className={styles.textInput}
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="np. j.kowalski"
              />
              {!!login && !loginValid && (
                <span className={styles.fieldError}>
                  Dozwolone litery, cyfry oraz . _ - @ (od 3 do 100 znaków).
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                {councilor ? "Nowe hasło (opcjonalnie)" : "Hasło"}
              </label>
              <input
                className={styles.textInput}
                type="password"
                value={password}
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              {!passwordValid && (
                <span className={styles.fieldError}>
                  Hasło musi mieć co najmniej 8 znaków.
                </span>
              )}
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Funkcja w radzie</label>
              <select
                className={styles.textInput}
                value={role}
                onChange={(e) => setRole(Number(e.target.value) as ECouncilorRole)}
              >
                <option value={ECouncilorRole.None}>Radny</option>
                <option value={ECouncilorRole.President}>Przewodniczący rady</option>
                <option value={ECouncilorRole.Deputy}>Zastępca przewodniczącego</option>
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Kadencja</label>
              <div className={styles.fieldRow}>
                <input
                  className={styles.textInput}
                  type="date"
                  value={cadenceStart}
                  onChange={(e) => setCadenceStart(e.target.value)}
                />
                <input
                  className={styles.textInput}
                  type="date"
                  value={cadenceEnd}
                  onChange={(e) => setCadenceEnd(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.radioRow}>
            <label className={styles.radioOption}>
              <input type="radio" checked={male} onChange={() => setMale(true)} />
              Mężczyzna
            </label>
            <label className={styles.radioOption}>
              <input type="radio" checked={!male} onChange={() => setMale(false)} />
              Kobieta
            </label>

            <label className={styles.radioOption} style={{ marginLeft: "auto" }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Konto aktywne
            </label>
          </div>

          {councilor && !isActive && councilor.IsActive && (
            <span className={styles.fieldError}>
              Wyłączenie konta natychmiast wyloguje radnego z portalu.
            </span>
          )}

          {!!cameras.length && (
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Kamera</label>
                <select
                  className={styles.textInput}
                  value={cameraId}
                  onChange={(e) => setCameraId(e.target.value)}
                >
                  <option value="">pierwsza skonfigurowana</option>
                  {cameras.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.Label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Preset kamery</label>
                <input
                  className={styles.textInput}
                  value={cameraPreset}
                  inputMode="numeric"
                  onChange={(e) =>
                    setCameraPreset(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="numer presetu"
                />
                <span className={styles.fieldLabel}>
                  Numer widoku zaprogramowanego w kamerze na miejsce tego radnego.
                  Puste = kamera zostaje na widoku ogólnym.
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text={councilor ? "Zapisz" : "Dodaj"}
            loading={loading}
            disabled={!canApply}
            onClick={submit}
          />
        </div>
      </div>
    </div>
  );
};

export default CouncilorModal;
